import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  Tag,
  FileText,
  MessageSquare,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Mail,
  UserCog,
  Layout
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAllowedPages, iconMap } from '../../utils/permissionCheck';

const Sidebar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useTheme();
  const { user } = useAuth();

  const isRTL = i18n.language === 'ar';
  const [allowedPages, setAllowedPages] = useState([]);

  // تحميل الصفحات المسموحة عند تغيير المستخدم
  useEffect(() => {
    if (user) {
      const pages = getAllowedPages(user);
      setAllowedPages(pages);
    } else {
      setAllowedPages([]);
    }
  }, [user]);

  // تحويل الصفحات المسموحة إلى عناصر التنقل
  const navigationItems = allowedPages.map(page => ({
    name: isRTL ? page.name_ar : page.name,
    href: page.path === '/' ? '/dashboard' : page.path,
    icon: iconMap[page.icon] || LayoutDashboard,
    current: location.pathname === page.path ||
             (page.path === '/' && location.pathname === '/dashboard') ||
             (page.path !== '/' && location.pathname.startsWith(page.path))
  }));

  return (
    <div className={`
      fixed top-0 ${isRTL ? 'right-0' : 'left-0'} h-full bg-white shadow-lg border-r border-gray-200 z-30
      transition-all duration-300 ease-in-out theme-transition
      ${sidebarCollapsed ? 'w-16' : 'w-64'}
    `}>
      {/* Logo Section */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
        {!sidebarCollapsed && (
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <div
              className="h-8 w-8 rounded-dynamic flex items-center justify-center"
              style={{ backgroundColor: 'var(--primary-color)' }}
            >
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-900 theme-transition">
                {isRTL ? 'التسويق الإلكتروني' : 'Email Marketing'}
              </span>
              <span className="text-xs text-gray-500 theme-transition">
                {isRTL ? 'نظام الإدارة' : 'Management System'}
              </span>
            </div>
          </div>
        )}
        
        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          {sidebarCollapsed ? (
            isRTL ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />
          ) : (
            isRTL ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-8 px-3">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                children={({ isActive }) => (
                  <div
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-dynamic transition-colors theme-transition
                      ${isActive
                        ? 'border-r-2'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                      ${sidebarCollapsed ? 'justify-center' : ''}
                    `}
                    style={isActive ? {
                      backgroundColor: 'var(--primary-color)',
                      borderColor: 'var(--primary-color)',
                      color: 'white'
                    } : {}}
                    title={sidebarCollapsed ? item.name : ''}
                  >
                    <Icon
                      className={`
                        h-5 w-5 flex-shrink-0
                        ${sidebarCollapsed ? '' : (isRTL ? 'ml-3' : 'mr-3')}
                      `}
                      style={isActive ? { color: 'white' } : {}}
                    />
                    {!sidebarCollapsed && (
                      <span
                        className="truncate"
                        style={isActive ? { color: 'white' } : {}}
                      >
                        {item.name}
                      </span>
                    )}
                  </div>
                )}
              />
            );
          })}
        </div>
      </nav>

      {/* Collapsed Logo */}
      {sidebarCollapsed && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--primary-color)' }}
          >
            <Mail className="h-5 w-5 text-white" />
          </div>
        </div>
      )}

      {/* Version Info */}
      {!sidebarCollapsed && (
        <div className="absolute bottom-4 left-4 right-4">
          <div className="text-xs text-gray-400 text-center">
            <p>{isRTL ? 'الإصدار' : 'Version'} 1.0.0</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
